import { shallowRef, triggerRef } from 'vue';

import { tokenStorage } from '@/platform/auth-runtime/token-storage';
import { clock } from '@/shared/lib/clock/clock';
import { WIRE_HEADERS } from '@/shared/lib/http/wire-headers';
import { parsePrivateLink } from '@/shared/utils/private-link';

// shallowRef + triggerRef so watchers see EVERY push, including two
// identical frames in a row. A regular ref short-circuits on same-primitive
// assignment, which silently drops repeated permission / account /
// notification pushes — backend sends the same JSON twice in legitimate
// cases (state change → revert → state change) and consumers would never
// rerun their handler for the second frame.
export const wsData = shallowRef<unknown>(null);

const INITIAL_RETRY_GAP = 1000;
const MAX_RETRY_GAP = 8000;
// Server pushes "hb" every 15s; 45s of inbound silence on an OPEN socket
// means the connection is half-open (no onclose fires for these).
const CONN_DEAD_MS = 45_000;
const LIVENESS_TICK_MS = 15_000;

let socket: WebSocket | null = null;
let hbTimer: ReturnType<typeof setInterval> | null = null;
let livenessTimer: ReturnType<typeof setInterval> | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let lastInboundTime = 0;
let retryGap = INITIAL_RETRY_GAP;
let stopped = true;

// One id per tab, reused across reconnects (matches legacy's module-level id).
// A fresh id per reconnect makes the backend treat each retry as a new client.
const clientId = `ws_${crypto.randomUUID()}`;

async function buildSignedUrl(ubid: string, token: string): Promise<string> {
  // Server-aligned timestamp from the /time-synced clock (mirrors REST
  // genRequestTicket). Raw Date.now() would let a client with a skewed
  // wall clock pass the REST freshness check but get the WS handshake
  // rejected by the same signature window — leaving forced-logout,
  // notification, and permission pushes disconnected.
  const ts = clock.now();
  const nonce = crypto.randomUUID();
  const hash = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(`${ubid}${token}${nonce}${ts}`),
  );
  const signature = Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
  // Private-link installs sit on `<tenant>.privatelink.celerdata.com`; the
  // WS handshake must reach the private-link backend instead of the public
  // cloud-api host. Without this, private-link sessions sign in via REST but
  // never receive WS pushes.
  const base = parsePrivateLink(import.meta.env.VITE_WS_URL);
  const wsBase = base.startsWith('/') ? `ws://${location.host}${base}` : base;
  const params = new URLSearchParams({
    client_id: clientId,
    [WIRE_HEADERS.ubid]: ubid,
    [WIRE_HEADERS.token]: token,
    [WIRE_HEADERS.nonce]: nonce,
    [WIRE_HEADERS.timestamp]: String(ts),
    [WIRE_HEADERS.signature]: signature,
  });
  return `${wsBase}?${params}`;
}

function clearHeartbeat(): void {
  if (hbTimer) {
    clearInterval(hbTimer);
    hbTimer = null;
  }
}

function stopLivenessWatch(): void {
  if (livenessTimer) {
    clearInterval(livenessTimer);
    livenessTimer = null;
  }
}

function startLivenessWatch(): void {
  stopLivenessWatch();
  lastInboundTime = Date.now();
  livenessTimer = setInterval(() => {
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    if (lastInboundTime && Date.now() - lastInboundTime >= CONN_DEAD_MS) {
      // Half-open dead connection. close() → onclose → reconnect path.
      socket.close();
    }
  }, LIVENESS_TICK_MS);
}

function clearReconnect(): void {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
}

async function connect(): Promise<void> {
  if (stopped) return;
  if (socket) return;
  const auth = tokenStorage.read();
  if (!auth) return;
  const url = await buildSignedUrl(auth.ubid, auth.token);
  // Session may have changed while WebCrypto was signing the URL:
  // stopWs() could have been called (logout / account switch) OR the
  // stored token itself rotated. Opening this now would race the new
  // session's socket or deliver pushes scoped to the previous account.
  if (stopped) return;
  const current = tokenStorage.read();
  if (!current || current.token !== auth.token || current.ubid !== auth.ubid) return;
  socket = new WebSocket(url);
  socket.onopen = () => {
    retryGap = INITIAL_RETRY_GAP;
    clearHeartbeat();
    startLivenessWatch();
    hbTimer = setInterval(() => {
      // Guard the send: between the 25s tick and now the socket could have
      // moved to CLOSING/CLOSED (server-initiated close, transient drop). A
      // bare `socket?.send` would throw InvalidStateError in that window.
      // Skip the tick and let onclose handle the reconnect path.
      if (socket?.readyState === WebSocket.OPEN) socket.send('hb');
    }, 25000);
  };
  socket.onmessage = (event) => {
    lastInboundTime = Date.now();
    // Server sometimes emits `"hb"\n` (JSON-encoded + trailing newline)
    // instead of bare `hb`; both must be filtered or they leak into wsData.
    const data = event.data;
    if (typeof data === 'string' && (data === 'hb' || data.trim() === '"hb"')) return;
    // shallowRef short-circuits on Object.is equal values. Force a notify
    // when the new frame is identical to the last so watchers see EVERY push
    // — backend sends the same JSON twice in legitimate cases (state flips
    // back) and consumers would otherwise drop the second.
    const sameAsPrev = Object.is(wsData.value, data);
    wsData.value = data;
    if (sameAsPrev) triggerRef(wsData);
  };
  socket.onclose = () => {
    clearHeartbeat();
    stopLivenessWatch();
    socket = null;
    if (stopped) return;
    reconnectTimer = setTimeout(() => void connect(), retryGap);
    retryGap = Math.min(retryGap * 2, MAX_RETRY_GAP);
  };
}

export async function startWs(): Promise<void> {
  stopped = false;
  await connect();
}

export function stopWs(): void {
  // clearReconnect: a stopWs() landing between onclose and the queued
  // setTimeout fires would otherwise reconnect a logged-out session.
  stopped = true;
  clearHeartbeat();
  stopLivenessWatch();
  clearReconnect();
  socket?.close();
  socket = null;
}
